import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react'
import {PIXI} from '@colonist/pixi'
import {StorageWrapper} from '../utils/storage'
import {Sound} from '../utils/sound'
import {useHotkeys} from 'react-hotkeys-hook'
import {clamp} from '@colonist/utils'
import {LobbyImage} from '@colonist/types'
import {TranslationObject} from '@colonist/i18n'
// eslint-disable-next-line no-restricted-imports
import {notificationController} from '../../../../code/client/shared/scripts/EntryShared'
// eslint-disable-next-line no-restricted-imports
import {NotificationContentType} from '../../../../code/client/shared/scripts/ui_utils/NotificationUtils'
import {useUIGameUserStatesContext} from './UIGameUserStatesContext'
import {useUIGamePauseContext} from '../core/UIGamePause/UIGamePauseContext'

interface UIGameUserSettingsContextProps {
    volume: number
    setVolume: (volume: number) => void
    isOpenSettings: boolean
    setIsOpenSettings: (isOpen: boolean) => void
    setCanvasOptions: (options: Partial<PIXI.IApplicationOptions>) => void
}

const UIGameUserSettingsContext = createContext<UIGameUserSettingsContextProps | undefined>(undefined)

export const useUIGameUserSettingsContext = () => {
    const context = useContext(UIGameUserSettingsContext)
    if(!context) throw new Error('useUIGameUserSettingsContext must be used within a UIGameUserSettingsProvider')
    return context
}

interface UIGameUserSettingsProviderProps {
    isOpenSettings: boolean
    setIsOpenSettings: (isOpen: boolean) => void
    setCanvasOptions: (options: Partial<PIXI.IApplicationOptions>) => void
    children: ReactNode
}

export function UIGameUserSettingsProvider({
    isOpenSettings,
    setIsOpenSettings,
    setCanvasOptions,
    children,
}: UIGameUserSettingsProviderProps) {
    const [volume, setVolume] = useState(StorageWrapper.getMasterVolume(Sound.DEFAULT_VOLUME))
    const {hasOnlyOneHumanPlayer} = useUIGameUserStatesContext()
    const {requestPause} = useUIGamePauseContext()

    const setVolumeWithClamp = (newVolume: number, showNotification = false) => {
        const clampedVolume = clamp(newVolume, 0, 1)
        setVolume(clampedVolume)

        if(showNotification) {
            const volumeDisplayLevel = Math.round(clampedVolume * 100)
            const masterVolumeChangedText: TranslationObject = {
                key: 'strings:game.settings.shortcutPopups.volumeLevel',
                options: {count: volumeDisplayLevel},
            }
            notificationController.showNotification(LobbyImage.Audio,
                masterVolumeChangedText,
                2500,
                undefined,
                undefined,
                NotificationContentType.Volume)
        }
    }

    const toggleMute = () => {
        const newLevel = volume > 0 ? 0 : 1
        setVolume(newLevel)

        const mutedText: TranslationObject = newLevel > 0 ?
            {key: 'strings:game.settings.shortcutPopups.soundUnmuted'}
            : {key: 'strings:game.settings.shortcutPopups.soundMuted'}
        notificationController.showNotification(LobbyImage.Audio, mutedText)
    }

    const VOLUME_INCREMENT = 0.1
    useHotkeys('ArrowUp', () => {
        if(!StorageWrapper.getKeyboardShortcuts()) return
        setVolumeWithClamp(volume + VOLUME_INCREMENT, true)
    })
    useHotkeys('ArrowDown', () => {
        if(!StorageWrapper.getKeyboardShortcuts()) return
        setVolumeWithClamp(volume - VOLUME_INCREMENT, true)
    })
    useHotkeys('m', () => {
        if(!StorageWrapper.getKeyboardShortcuts()) return
        toggleMute()
    })

    useEffect(() => {
        Sound.setMasterVolume(volume)
    }, [volume])

    useEffect(() => {
        if(isOpenSettings && hasOnlyOneHumanPlayer === true) {
            requestPause()
        }
    }, [isOpenSettings, hasOnlyOneHumanPlayer])

    return (
        <UIGameUserSettingsContext.Provider value={{
            volume: volume,
            setVolume: setVolumeWithClamp,
            isOpenSettings: isOpenSettings,
            setIsOpenSettings: setIsOpenSettings,
            setCanvasOptions: setCanvasOptions,
        }}>
            {children}
        </UIGameUserSettingsContext.Provider>
    )
}
